import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { PendingSuggestionsChanges } from "../../db/schema";
import Button from "../common/Button";

interface PendingChangesBannerProps {
  pendingData: PendingSuggestionsChanges;
  onReview: () => void;
  onDiscard: () => void;
  onRetry: () => void;
  isRetrying?: boolean;
}

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const seconds = Math.max(0, Math.floor((now - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

export default function PendingChangesBanner({
  pendingData,
  onReview,
  onDiscard,
  onRetry,
  isRetrying,
}: PendingChangesBannerProps) {
  const isError = !!pendingData.error;
  const changeCount = pendingData.changes?.length || 0;
  const isEmpty = !isError && changeCount === 0;
  const relative = pendingData.createdAt
    ? formatRelativeTime(pendingData.createdAt)
    : "";

  if (isEmpty) {
    return (
      <div className="mb-3 rounded-lg border border-gray-300 dark:border-base-300 bg-base-200 p-3">
        <div className="flex items-start gap-2">
          <InformationCircleIcon className="size-5 text-gray-600 dark:text-base-content/70 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-base-content">
              No changes generated
            </p>
            <p className="text-xs text-gray-700 dark:text-base-content/80 mt-0.5">
              The audio didn't produce any applicable changes for this report.
              Discard to try again.
            </p>
            {relative && (
              <p className="text-[11px] text-gray-600 dark:text-base-content/70 mt-0.5">{relative}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2 mt-2">
          <Button
            variant="primary"
            size="sm"
            className="flex-1"
            onClick={onDiscard}
          >
            Discard
          </Button>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mb-3 rounded-lg border border-amber-300 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 p-3">
        <div className="flex items-start gap-2">
          <ExclamationTriangleIcon className="size-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              Add to Report failed
            </p>
            <p className="text-xs text-amber-800 mt-0.5 break-words dark:text-amber-300">
              {pendingData.error}
            </p>
            {relative && (
              <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-0.5">{relative}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2 mt-2">
          <Button
            variant="primary"
            size="sm"
            className="flex-1"
            onClick={onRetry}
            loading={isRetrying}
            disabled={isRetrying}
          >
            Retry
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDiscard}
            disabled={isRetrying}
          >
            Discard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3 rounded-lg border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/40 p-3">
      <div className="flex items-start gap-2">
        <CheckCircleIcon className="size-5 text-blue-600 dark:text-blue-300 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">
            {changeCount} pending change{changeCount === 1 ? "" : "s"} ready to review
          </p>
          {relative && (
            <p className="text-[11px] text-blue-700 dark:text-blue-300 mt-0.5">
              From audio uploaded {relative}
            </p>
          )}
        </div>
      </div>
      <div className="flex gap-2 mt-2">
        <Button
          variant="primary"
          size="sm"
          className="flex-1"
          onClick={onReview}
        >
          Review Now
        </Button>
        <Button variant="outline" size="sm" onClick={onDiscard}>
          Discard
        </Button>
      </div>
    </div>
  );
}
